import React from 'react';
import { Envelope, Question, Lifebuoy } from '@phosphor-icons/react';
import { SUPPORT_EMAIL, buildSupportMailto } from '../config/appConfig';

/**
 * SupportLink — inline clickable mailto link.
 * Props:
 *   subject: optional prefilled subject line (default "CyBoard Support Request")
 *   body:    optional prefilled body
 *   label:   optional custom label (default: the support email address)
 *   className: optional styling override
 */
export function SupportLink({ subject, body, label, className = '' }) {
  const href = buildSupportMailto({ subject, body });
  return (
    <a
      href={href}
      className={`text-[#9D4CDD] hover:text-[#B26EEA] underline-offset-2 hover:underline transition-colors ${className}`}
      data-testid="support-link"
    >
      {label || SUPPORT_EMAIL}
    </a>
  );
}

/**
 * SupportButton — pill-shaped "Contact Support" CTA.
 * Use in settings, profile menus, onboarding final step, error states.
 */
export function SupportButton({ subject, body, variant = 'primary', size = 'md', label = 'Contact Support', icon = true, className = '' }) {
  const href = buildSupportMailto({ subject, body });
  const base = 'inline-flex items-center gap-2 rounded-lg font-medium transition-colors';
  const sizes = {
    sm: 'text-[11px] px-2.5 py-1.5',
    md: 'text-[13px] px-3.5 py-2',
    lg: 'text-sm px-5 py-2.5',
  };
  const variants = {
    primary: 'bg-[#9D4CDD]/15 text-[#9D4CDD] hover:bg-[#9D4CDD]/25',
    ghost: 'text-[#9D4CDD] hover:bg-white/[0.04]',
    outline: 'border border-[#9D4CDD]/30 text-[#9D4CDD] hover:bg-[#9D4CDD]/10',
  };
  return (
    <a
      href={href}
      className={`${base} ${sizes[size]} ${variants[variant]} ${className}`}
      data-testid="support-button"
    >
      {icon && <Lifebuoy size={size === 'sm' ? 12 : size === 'lg' ? 16 : 14} weight="duotone" />}
      {label}
    </a>
  );
}

/**
 * SupportFooter — minimal footer strip. Drop at bottom of layouts.
 * Use size="compact" for tight layouts (mobile above nav).
 */
export function SupportFooter({ variant = 'default', className = '' }) {
  if (variant === 'compact') {
    return (
      <div className={`text-center text-[10px] text-[#444] py-2 ${className}`} data-testid="support-footer">
        <div>Need help? <SupportLink className="text-[#666] hover:text-[#9D4CDD]" label={SUPPORT_EMAIL} /></div>
        <div className="mt-1 flex justify-center gap-3">
          <a href="/privacy" className="text-[#555] hover:text-[#9D4CDD]" data-testid="privacy-link">Privacy</a>
          <span className="text-[#333]">·</span>
          <a href="/terms" className="text-[#555] hover:text-[#9D4CDD]" data-testid="terms-link">Terms</a>
        </div>
      </div>
    );
  }
  return (
    <footer className={`border-t border-white/[0.04] mt-6 pt-4 pb-6 text-center ${className}`} data-testid="support-footer">
      <div className="flex items-center justify-center gap-1.5 text-[11px] text-[#555]">
        <Envelope size={12} weight="duotone" className="text-[#666]" />
        <span>Support:</span>
        <SupportLink className="text-[#9D4CDD] hover:text-[#B26EEA]" label={SUPPORT_EMAIL} />
      </div>
      <div className="mt-2 flex items-center justify-center gap-3 text-[11px] text-[#555]">
        <a href="/privacy" className="hover:text-[#9D4CDD]" data-testid="privacy-link">Privacy Policy</a>
        <span className="text-[#333]">·</span>
        <a href="/terms" className="hover:text-[#9D4CDD]" data-testid="terms-link">Terms of Service</a>
      </div>
    </footer>
  );
}

/**
 * ErrorWithSupport — standard error display that always includes a mailto CTA.
 * Keep error messaging consistent across the app.
 */
export function ErrorWithSupport({ title = 'Something went wrong', message, subject, className = '' }) {
  return (
    <div className={`rounded-xl border border-[#EF4444]/25 bg-[#EF4444]/5 px-4 py-3 ${className}`}
      data-testid="error-with-support">
      <div className="flex items-start gap-2">
        <Question size={16} weight="duotone" className="text-[#EF4444] flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold text-[#EF4444]">{title}</p>
          {message && <p className="text-[12px] text-[#888] mt-0.5">{message}</p>}
          <p className="text-[11px] text-[#666] mt-2">
            If this keeps happening, contact{' '}
            <SupportLink subject={subject || `CyBoard Issue: ${title}`} label="support" className="text-[#9D4CDD]" />.
          </p>
        </div>
      </div>
    </div>
  );
}
